// @ts-check
// @ts-ignore - no type declaration for the "three" module; only a global THREE namespace exists.
import THREE from "three";
import { OutOfCoreTaskBase } from "../OutOfCoreTaskBase";
import { MeshFlags } from "../../../MeshFlags";
import { getDataBucketName, getLod0BucketName, leafHashKey, lod0HashKey } from "../../../../../file-loaders/rcs/RcsOpfsCache.js";
import { rcsWorkerPool } from "../../../../../file-loaders/rcs/RcsWorkerPool";

/** @import {BvhNode} from "../../BvhNode" */
/** @import {RcsFileLoader, RcsDecodedPoints, RcsLevelDecodeResult } from "../../../../../file-loaders/rcs/RcsFileLoader" */
/** @import { ParentLodRange, RcsPackage } from "../../../../../file-loaders/rcs/RcsPackage" */
/** @import { Model } from "../../../../../application/Model" */
/** @import { Viewer3DImpl } from "../../../../../application/Viewer3DImpl" */
/** @import {UmbrellaLeafNode, RcsExtractorCore} from "../../../../../file-loaders/rcs/RcsExtractorCore" */
/** @import { LeanBufferGeometry } from "../../../BufferGeometry" */
/** @import { RenderBatch } from "../../../RenderBatch" */
/** @import { FragmentList } from "../../../FragmentList" */

/** Throttle window for RCS load-time invalidation (ms). */
const REDRAW_INTERVAL_MS = 2000;

// Reusable scratch objects for transforming HLOD points into parent space to avoid GC overhead.
const _scratchParentInverse = new THREE.Matrix4();
const _scratchCombinedTransform = new THREE.Matrix4();
const _scratchLeafTransform = new THREE.Matrix4();
const _scratchParentTransform = new THREE.Matrix4();
const _scratchPoint = new THREE.Vector3();

/**
 * Task for loading RCS octree nodes on-demand via the OutOfCoreTileManager.
 * Each task loads all fragments belonging to a single BVH node.
 */
export class LoadRcsNodeTask extends OutOfCoreTaskBase {

  /** @type {number} - Timestamp of last invalidate call for throttling */
  static _lastInvalidateTime = 0;
  /** @type {number|undefined} - Timeout ID for debounced redraw */
  static _redrawTimeout = undefined;

  /**
   * Create a task to load RCS geometry for a BVH node.
   * @param {BvhNode} node - The BVH node to process in this task
   */
  constructor(node) {
    super(node.outOfCoreTileManager);
    /** @type {BvhNode} - The BVH node being processed */
    this.node = node;
    /** @type {Array<number>} - Fragment IDs this task activated */
    this.activatedFragments = [];
  }

  /**
   * Load RCS node data for all fragments in this BVH node.
   * Uses async loading to yield control and respect time budget.
   * @returns {Promise<void>}
   */
  async execute() {
    const model = /** @type {Model} */this.node.model;
    const rcsLoader = /** @type {RcsFileLoader} */model.loader;
    const pkg = /** @type {RcsPackage} */model.getData();
    const nodeId = this.node.nodeId;
    const renderBatch = model.getIterator().getGeomScenes()[nodeId];
    const renderBatchIndices = renderBatch.getIndices();

    // Load geometry for all fragments in this batch
    for (let i = renderBatch.start; i < renderBatch.lastItem; i++) {
      const fragId = renderBatchIndices[i];
      if (fragId === undefined) continue;

      if (pkg.isInnerNode(nodeId)) {
        // Inner node with HLOD points — create empty geometry that will be filled
        // by child nodes' _loadFragmentWithHierarchicalLOD
        const fragmentList = model.getFragmentList();
        const pointCount = pkg.getNodePointCount(nodeId);
        if (pointCount > 0) {
          // Try loading inner node from OPFS cache first.
          // Cache hit reconstructs geometry directly from cached point data;
          // cache miss (or a failed read) falls back to creating an empty fragment
          // that gets filled by child nodes' _loadFragmentWithHierarchicalLOD.
          let cachedFromOpfs = false;
          try {
            cachedFromOpfs = await this._loadInnerNodeFromCache(rcsLoader, fragId, nodeId, pointCount);
          } catch (error) {
            console.warn('RCS: failed to load inner node from OPFS cache', error);
          }
          if (this.canceled) return;
          if (!cachedFromOpfs && !fragmentList.getGeometry(fragId)) {
            rcsLoader._addAndActivateFragment(fragId, { pointCount, innerFragment: true });

            // TODO: we currently never unload the inner nodes (by bumping their screenspace error)
            //       and we don't add them to the activatedFragments array here (so they stay loaded forever).
            //       Unloading them would mean that we have to refetch all umbrella nodes, which is quite expensive.
            //       Once we have support for the OPFS cache, we should allow unloading of cached nodes.
            //       Note: The RCSFileLoader uses the assumption these stay loaded for load-progress reporting.
            //             If this is changed, the load progress needs to be updated.
            //this.activatedFragments.push(fragId);
          }
        } else {
          // If there are no points in the inner node, mark the fragment as empty
          this._markFragmentEmpty(fragmentList, fragId);
        }
      } else if (pkg.getNodeLodLevel(nodeId) === 0 && pkg.getNodeUmbrellaIndex(nodeId) >= 0) {
        // LOD-0 umbrella leaf: distributes points to parent nodes via hierarchical LOD.
        const lodLevel = pkg.getNodeLodLevel(nodeId);

        const umbrellaNodeIndex = pkg.getNodeUmbrellaIndex(nodeId);
        const leafNode = pkg.getUmbrellaLeafNode(umbrellaNodeIndex);

        await this._loadFragmentWithHierarchicalLOD(
          rcsLoader, pkg, fragId, nodeId, umbrellaNodeIndex, leafNode, lodLevel
        );

        // Bail out if the task was canceled during the await: undo() has already
        // run, so don't start loading further fragments.
        if (this.canceled) return;
      } else {
        // Standard LOD fragment — load points directly into this fragment's geometry
        const lodLevel = pkg.getNodeLodLevel(nodeId);

        await this._loadFragmentWithRange(rcsLoader, pkg, fragId, nodeId, lodLevel);

        // Bail out if the task was canceled during the await: undo() has already
        // run, so don't start loading further fragments.
        if (this.canceled) return;
      }
    }

    // For OTG, redraws are scheduled by the OTGLoader in a different place. For RCS, this
    // invalidation (and the throttled follow-up in _scheduleRedraw) is needed so new
    // geometry appears on screen as it is being loaded.
    this._scheduleRedraw(rcsLoader);

    // Notify the loader that a coarse node finished. We do this here (rather than inside
    // _addAndActivateFragment) so the count also advances for coarse nodes whose load was
    // skipped because the SVO range was empty — those still represent completed work and
    // must contribute to progress, otherwise the indicator would stall at <100%.
    if (pkg.isCoarseNode(nodeId)) {
      rcsLoader.onCoarseNodeLoaded();
    }
  }

  /**
   * Mark a fragment as empty: it has no points of its own, so it must never be rendered
   * or treated as loadable. Sets MESH_NOTLOADED and clears MESH_SWAPPED_OUT_OF_CORE, so the
   * fragment is neither resident (isInstanceLoaded === false) nor a swap-in candidate.
   * @private
   * @param {FragmentList} fragmentList - The fragment list
   * @param {number} fragId - Fragment ID to mark as empty
   */
  _markFragmentEmpty(fragmentList, fragId) {
    fragmentList.setFlagFragment(fragId, MeshFlags.MESH_NOTLOADED, true);
    fragmentList.setFlagFragment(fragId, MeshFlags.MESH_SWAPPED_OUT_OF_CORE, false);
  }

  /**
   * Activate a leaf LOD fragment and record it in {@link activatedFragments} so
   * {@link undo} can swap it back out on demotion. Inner HLOD nodes use a separate
   * path ({@link innerFragment}) and are intentionally not tracked here.
   * @private
   * @param {RcsFileLoader} rcsLoader - The RCS loader instance
   * @param {FragmentList} fragmentList - The fragment list
   * @param {number} fragId - Fragment to activate
   * @param {Object} options - Point data passed to {@link RcsFileLoader._addAndActivateFragment}
   * @param {number} options.pointCount - Number of points
   * @param {Float32Array} [options.interleavedData] - Interleaved XYZRGB data
   * @returns {LeanBufferGeometry} The created geometry
   */
  _activateTrackedFragment(rcsLoader, fragmentList, fragId, options) {
    fragmentList.setFlagFragment(fragId, MeshFlags.MESH_SWAPPED_OUT_OF_CORE, false);
    const geometry = rcsLoader._addAndActivateFragment(fragId, options);
    this.activatedFragments.push(fragId);
    return geometry;
  }

  /**
   * Load RCS fragment with a specified point range.
   * Standard loading path: fetches SVO data, decodes it, and creates geometry
   * directly for this fragment.
   * @private
   * @param {RcsFileLoader} rcsLoader - The RCS loader instance
   * @param {RcsPackage} pkg - The RCS package with model data
   * @param {number} fragId - Fragment ID to load
   * @param {number} nodeId - BVH node ID
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @returns {Promise<number>} Actual number of points loaded
   */
  async _loadFragmentWithRange(rcsLoader, pkg, fragId, nodeId, lodLevel) {
    const umbrellaNodeIndex = pkg.getNodeUmbrellaIndex(nodeId);
    const leafNode = pkg.getUmbrellaLeafNode(umbrellaNodeIndex);
    const model = /** @type {Model} */rcsLoader.model;
    const fragmentList = model.getFragmentList();

    // Calculate SVO data location and fetch + decode in worker
    const { url, offset, length } = this._calculateSvoDataLocation(
      rcsLoader, pkg, umbrellaNodeIndex, lodLevel
    );

    // If there's no data to load (fragment doesn't have points in this range), skip.
    if (length === 0) {
      // No SVO data for this range: the fragment has no points, mark it empty
      this._markFragmentEmpty(fragmentList, fragId);
      return 0;
    }

    // The worker reads the raw SVO bytes from the OPFS cache (or fetches them and
    // persists them on a miss) before decoding.
    const opfsCache = rcsLoader.opfsCache;
    const cacheHashKey = opfsCache.isLodLevelCacheable(lodLevel) ?
    leafHashKey(opfsCache.cacheId, umbrellaNodeIndex, lodLevel) :
    null;
    const bucketName = cacheHashKey ? getDataBucketName(opfsCache.cacheId) : null;

    // Decode SVO payload in worker (worker will fetch the data)
    const localPoints = await this._decodeSvoPayloadInWorker(
      url, offset, length, leafNode, lodLevel, pkg.directModeLevelsInLod0,
      cacheHashKey, bucketName
    );

    // The task may have been canceled (node demoted/freed) while the worker decode
    // was in flight. undo() has already run by then, so we must not activate any new
    // geometry here — doing so would leak a fragment that nothing will deactivate.
    // The synchronous check right after the await is race-free (no interleaving).
    if (this.canceled) return 0;

    // No points in the fragment, mark it empty
    if (localPoints.pointCount === 0) {
      this._markFragmentEmpty(fragmentList, fragId);
      return 0;
    }

    // Create THREE.js geometry from interleaved point data and activate the fragment.
    const geometry = this._activateTrackedFragment(rcsLoader, fragmentList, fragId, localPoints);

    // The resource cost is only estimated. If there are deleted points in the node,
    // we will need less memory than indicated by the umbrella node.
    console.assert(this.getResourceCost() >= geometry.getByteSize());

    return localPoints.pointCount;
  }

  /**
   * Load fragment with hierarchical LOD distribution.
   * Decodes points level-by-level and distributes them to parent BVH nodes
   * for coarse LOD visualisation.
   * @private
   * @param {RcsFileLoader} rcsLoader - The RCS loader instance
   * @param {RcsPackage} pkg - The RCS package with model data
   * @param {number} fragId - Fragment ID to load
   * @param {number} nodeId - BVH node ID
   * @param {number} umbrellaNodeIndex - Index of the umbrella node
   * @param {UmbrellaLeafNode} leafNode - The leaf node metadata
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @returns {Promise<number>} Actual number of points loaded
   */
  async _loadFragmentWithHierarchicalLOD(rcsLoader, pkg, fragId, nodeId, umbrellaNodeIndex, leafNode, lodLevel) {
    const model = /** @type {Model} */rcsLoader.model;
    const fragmentList = model.getFragmentList();

    // Get the parent LOD ranges that define where each level's points go
    const parentLodRanges = pkg.getNodeParentLodRanges(nodeId);

    // If all parent inner nodes for this leaf are already cached from OPFS, the HLOD
    // pull-up into those inner nodes is redundant. The only thing that still needs to
    // render is the leaf's own fall-through (overflow) points, which are kept in the
    // retained data bucket (the transient raw LOD-0 bucket may already be deleted).
    if (parentLodRanges) {
      const innerNodesCachedSet = rcsLoader.opfsCache.innerNodesCachedSet;
      // Only inner nodes are ever written to the cache: both initInnerNodeCompletion
      // and the distribution loop below skip ranges whose target is not an inner node.
      // parentLodRanges can also contain "overflow" entries (e.g. the leaf's own bvh
      // node) that are never added to innerNodesCachedSet. Those must NOT block the
      // short-circuit — otherwise any leaf that has such an entry would re-run
      // the HLOD pull-up on every warm load even though all of its point-bearing
      // parents are already cached (purely redundant work).
      const allParentsCached = parentLodRanges.every(
        (r) => !pkg.isInnerNode(r.bvhNodeId) || innerNodesCachedSet.has(r.bvhNodeId)
      );
      if (allParentsCached) {
        const overflowCount = pkg.getNodePointCount(nodeId);
        if (overflowCount === 0) {
          // The leaf carries no points of its own; everything lives in cached parents.
          this._markFragmentEmpty(fragmentList, fragId);
          return 0;
        }
        // The leaf has fall-through points. Restore them from the retained bucket so
        // the leaf renders without re-fetching. If the blob is missing (e.g. an older
        // cache, or an interrupted cold load that never stored it), fall through to a
        // full decode below.
        const overflowVb = await rcsLoader.opfsCache.loadLeafOverflowPoints(umbrellaNodeIndex, overflowCount);
        if (this.canceled) return 0;
        if (overflowVb) {
          this._activateTrackedFragment(rcsLoader, fragmentList, fragId, {
            pointCount: overflowCount,
            interleavedData: overflowVb
          });
          return overflowCount;
        }
      }
    }

    const { url, offset, length } = this._calculateSvoDataLocation(
      rcsLoader, pkg, umbrellaNodeIndex, lodLevel
    );

    if (length === 0) {
      this._markFragmentEmpty(fragmentList, fragId);
      return 0;
    }

    // Cache the raw (undecoded) LOD-0 SVO bytes in the transient LOD-0 bucket so an
    // interrupted cold load can be resumed without re-fetching: the worker reads the
    // cache (or fetches on miss and persists the raw bytes) transparently. This bucket
    // is deleted once all inner nodes are cached; the leaf's fall-through points are
    // persisted separately below into the retained data bucket.
    const opfsCache = rcsLoader.opfsCache;
    const rawCacheHashKey = opfsCache.ready && opfsCache.cacheId ?
    lod0HashKey(opfsCache.cacheId, umbrellaNodeIndex) :
    null;
    const rawBucketName = rawCacheHashKey ? getLod0BucketName(opfsCache.cacheId) : null;

    // Decode points by LOD level using the specialized worker function.
    const result = await this._decodeSvoPayloadByLevelInWorker(
      url, offset, length, leafNode, lodLevel, pkg.directModeLevelsInLod0,
      rawCacheHashKey, rawBucketName
    );

    // The task may have been canceled (node demoted/freed) while the worker decode
    // was in flight. undo() has already run by then, so we must not activate the leaf
    // fragment or distribute points to parent geometries. The synchronous check right
    // after the await is race-free (no interleaving).
    if (this.canceled) return 0;

    if (!result.pointsByLevel || result.pointsByLevel.length === 0) {
      // There can be leaf nodes with no points in an RCS file, if all points are deleted.
      this._markFragmentEmpty(fragmentList, fragId);
      return 0;
    }

    if (!parentLodRanges) {
      console.warn(`[H-LOD] No parent LOD ranges found for node ${nodeId} (umbrella ${umbrellaNodeIndex})`);
      return 0;
    }

    const pointCount = pkg.getNodePointCount(nodeId);
    let leafGeometry = null;
    if (pointCount > 0) {
      // The LOD-0 leaf carries HLOD-overflow points: points that did not fit into any of
      // its ancestor inner nodes and spilled back onto the leaf itself (see _distributeHlod).
      // Activate the leaf so overflow points render — they are written into the leaf's
      // own geometry by the distribution loop below.
      leafGeometry = this._activateTrackedFragment(rcsLoader, fragmentList, fragId, { pointCount });

      // The resource cost is only estimated. If there are deleted points in the node,
      // we will need less memory than indicated by the umbrella node.
      console.assert(pkg.computeNodeMemorySize(nodeId) >= leafGeometry.getByteSize());
    } else {
      this._markFragmentEmpty(fragmentList, fragId);
    }

    // Get the leaf→viewer transform.
    fragmentList.getOriginalWorldMatrix(fragId, _scratchLeafTransform);

    const bvh = model.getIterator().getBVH();

    // Distribute each level's points to the appropriate parent node
    for (const levelData of result.pointsByLevel) {
      const { interleavedData: points, count } = levelData;
      if (!points || count === 0) continue; // Skip levels with no points

      // Find the parent LOD range for this level
      const parentRange = parentLodRanges.find((r) => r.level === levelData.level);
      if (!parentRange) continue;

      const parentFragId = bvh.nodes.getPrimStart(parentRange.bvhNodeId);
      const parentGeometry = fragmentList.getGeometry(parentFragId);
      if (!parentGeometry) {
        console.warn(`No geometry found for fragment ${parentFragId}`);
        continue;
      }

      // Use the fragment's stored translation, not the live BVH box: ModelIteratorBVH's
      // #updateBVHRec widens BVH boxes to the union of children's render-batch bounds,
      // so by the time HLOD writes points the box no longer matches the fragment's position.
      fragmentList.getOriginalWorldMatrix(parentFragId, _scratchParentTransform);

      // Transform points from leaf to parent coordinate system and write them
      // directly into the parent geometry's reserved range (no temporary buffer).
      this._transformAndWritePointsToGeometry(
        parentGeometry, points, count, _scratchLeafTransform, _scratchParentTransform,
        parentRange.rangeStart, parentRange.rangeEnd
      );

      // Update GPU buffer if geometry has already been uploaded
      const parentBatch = model.getIterator().getGeomScenes()[parentRange.bvhNodeId];
      this._uploadGeometryIfNeeded(
        rcsLoader, parentGeometry, parentRange, [parentBatch]);
    }

    // Track inner node completion for OPFS caching
    rcsLoader.opfsCache.trackInnerNodeCompletion(rcsLoader, parentLodRanges, umbrellaNodeIndex);

    // Persist the leaf's own fall-through points (written into leafGeometry.vb by the
    // distribution loop above) to the retained data bucket, so a warm reload can render
    // them once the transient raw LOD-0 bucket has been deleted. Leaves without overflow
    // have no leafGeometry and store nothing.
    //
    // This is fire-and-forget and safe to fail: on the next warm reload the leaf simply
    // misses the overflow blob, falls through to a full re-decode (raw SVO refetched from
    // the network, since the transient bucket is gone) and re-runs the pull-up. That
    // re-distribution is idempotent — it overwrites the already-cached inner nodes' fixed
    // ranges with identical values and never re-stores them (guarded by innerNodesCachedSet)
    // — so a failed store only costs a redundant refetch of this one leaf, with no
    // corruption or double counting.
    if (leafGeometry && leafGeometry.vb) {
      rcsLoader.opfsCache.storeLeafOverflowPoints(umbrellaNodeIndex, leafGeometry.vb);
    }

    // Return the actual number of points loaded (sum across all levels)
    return result.pointsByLevel.reduce((sum, level) => sum + level.count, 0);
  }

  /**
   * Schedule a throttled redraw after loading.
   * @private
   * @param {RcsFileLoader} rcsLoader - Loader whose viewer should be invalidated
   */
  _scheduleRedraw(rcsLoader) {
    const viewerImpl = /** @type {Viewer3DImpl} */rcsLoader.viewer3DImpl;
    if (performance.now() - LoadRcsNodeTask._lastInvalidateTime > REDRAW_INTERVAL_MS) {
      viewerImpl.invalidate(false, true);
      LoadRcsNodeTask._lastInvalidateTime = performance.now();
    }

    if (LoadRcsNodeTask._redrawTimeout) {
      clearTimeout(LoadRcsNodeTask._redrawTimeout);
      LoadRcsNodeTask._redrawTimeout = undefined;
    }
    LoadRcsNodeTask._redrawTimeout = setTimeout(() => {
      viewerImpl.invalidate(false, true);
      LoadRcsNodeTask._lastInvalidateTime = performance.now();
    }, REDRAW_INTERVAL_MS);
  }

  /**
   * Upload geometry sub-range to GPU if already uploaded (non-streaming).
   * If geometry.streamingDraw is true, the CPU-side buffer (geometry.vb)
   * will be automatically uploaded on the next draw call — no action needed.
   * @private
   * @param {RcsFileLoader} rcsLoader - Loader owning the model and viewer
   * @param {THREE.BufferGeometry} geometry - Parent inner-node geometry receiving uploaded vertices
   * @param {ParentLodRange} range - Sub-range within `geometry.vb` that was just written
   * @param {RenderBatch[]} [renderBatches] - Batches owning the geometry, for targeted render-bundle invalidation
   */
  _uploadGeometryIfNeeded(rcsLoader, geometry, range, renderBatches) {
    // streamingDraw is tri-state: false, true, or the STREAMING_DRAW_ONCE_DURING_UPLOAD
    // If true, geometry is drawn from the CPU side buffer and there is nothing to do.
    // If STREAMING_DRAW_ONCE_DURING_UPLOAD (WebGL only) a single frame of streaming draw
    // is performed before swapping to a persistent GPU buffer which needs to be updated here.
    if (geometry.streamingDraw === true) return;

    const renderer = /** @type {Viewer3DImpl} */rcsLoader.viewer3DImpl._webglrender;
    if (!renderer) return;

    if (geometry.ib) {
      // WebGL relies on an index buffer.
      const alignmentSize = 4 / geometry.ib.BYTES_PER_ELEMENT;
      renderer.uploadGeometry(geometry, {
        vertexStart: range.rangeStart,
        vertexEnd: range.rangeEnd,
        indexStart: Math.floor(range.rangeStart / alignmentSize) * alignmentSize,
        indexEnd: Math.ceil(range.rangeEnd / alignmentSize) * alignmentSize,
        renderBatches
      });
    } else {
      // WebGPU doesn't use an index buffer.
      renderer.uploadGeometry(geometry, {
        vertexStart: range.rangeStart,
        vertexEnd: range.rangeEnd,
        renderBatches
      });
    }
  }

  /**
   * Transform points from leaf coordinate system into parent coordinate system and
   * write them directly into the parent geometry's reserved range.
   *
   * @private
   * @param {LeanBufferGeometry} geometry - Target geometry
   * @param {Float32Array} points - Interleaved point data (XYZRGB)
   * @param {number} count - Number of points
   * @param {THREE.Matrix4} leafTransform - Leaf node transform matrix
   * @param {THREE.Matrix4} parentTransform - Parent node transform matrix
   * @param {number} rangeStart - Start index in the geometry's vertex buffer
   * @param {number} rangeEnd - End index (exclusive) in the geometry's vertex buffer
   */
  _transformAndWritePointsToGeometry(geometry, points, count, leafTransform, parentTransform, rangeStart, rangeEnd) {
    // Get the interleaved vertex buffer (LMV format)
    const buffer = geometry.vb;
    if (!buffer) {
      console.error('Geometry missing interleaved vertex buffer (vb)');
      return;
    }

    // Verify we have enough space (allow some flexibility for deleted points)
    const rangeSize = rangeEnd - rangeStart;
    const actualCount = Math.min(count, rangeSize);

    if (count > rangeSize) {
      console.warn(`Point count ${count} exceeds allocated range size ${rangeSize}, using ${actualCount} points`);
    }

    // Combined transformation matrix: parent^-1 * leaf.
    // This transforms from leaf local space directly to parent local space.
    _scratchParentInverse.copy(parentTransform).invert();
    _scratchCombinedTransform.multiplyMatrices(_scratchParentInverse, leafTransform);

    // Transform each point and write it straight into the reserved range.
    // points/buffer format: [x, y, z, r, g, b, ...] with position at offset 0 and color
    // at offset 3 within each stride (see RcsFileLoader._createPointGeometry's vblayout).
    // Source decode and destination vb share the same stride, so a single value drives both.
    const stride = geometry.vbstride ?? 6;
    const destOffset = rangeStart * stride;
    for (let i = 0; i < actualCount; i++) {
      const srcIdx = i * stride;
      const dstIdx = destOffset + srcIdx;

      // Transform position
      _scratchPoint.set(points[srcIdx], points[srcIdx + 1], points[srcIdx + 2]);
      _scratchPoint.applyMatrix4(_scratchCombinedTransform);

      buffer[dstIdx] = _scratchPoint.x;
      buffer[dstIdx + 1] = _scratchPoint.y;
      buffer[dstIdx + 2] = _scratchPoint.z;

      // Copy color unchanged
      buffer[dstIdx + 3] = points[srcIdx + 3];
      buffer[dstIdx + 4] = points[srcIdx + 4];
      buffer[dstIdx + 5] = points[srcIdx + 5];
    }

    //  Note: vbNeedsUpdate mustn't be set here as it signals "full re-upload pending" not partial updates.
    // @ts-ignore do we need this?
    geometry.needsUpdate = true;
  }

  /**
   * Calculate the URL, byte offset, and byte length for SVO data.
   * Uses RcsExtractorCore._calculateRequiredDataSize for LOD-aware byte range.
   * @private
   * @param {RcsFileLoader} rcsLoader - The RCS loader instance
   * @param {RcsPackage} pkg - The RCS package with model data
   * @param {number} umbrellaNodeIndex - Index of the umbrella node
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @returns {{url: string|null, offset: number, length: number}}
   */
  _calculateSvoDataLocation(rcsLoader, pkg, umbrellaNodeIndex, lodLevel) {
    const extractor = /** @type {RcsExtractorCore} */rcsLoader.rcsExtractor;
    const leafNode = pkg.getUmbrellaLeafNode(umbrellaNodeIndex);

    // Calculate required data size based on LOD range
    const { offset: dataOffset, size } = extractor._calculateRequiredDataSize(
      leafNode, lodLevel, pkg.directModeLevelsInLod0
    );

    // Get voxel chunk info
    const voxelChunk = extractor.chunks.find((c) => c.chunkId === 1);
    if (!voxelChunk) {
      throw new Error('Voxel data chunk (ID=1) not found');
    }

    // Cumulative offset for this leaf. The per-leaf prefix sum is precomputed in
    // RcsPackage, so this is O(1) rather than re-scanning all preceding leaves.
    const leafDataOffset = Number(voxelChunk.fileOffsetStart) + pkg.getUmbrellaLeafDataOffset(umbrellaNodeIndex);

    // Convert relative URL to absolute URL for worker fetch().
    // Workers require absolute URLs for fetch().
    let absoluteUrl = extractor.source;
    if (absoluteUrl && typeof window !== 'undefined' && !this._isAbsoluteUrl(absoluteUrl)) {
      absoluteUrl = new URL(absoluteUrl, window.location.href).href;
    }

    return {
      url: absoluteUrl,
      offset: leafDataOffset + dataOffset,
      length: size
    };
  }

  /**
   * Check if a URL is absolute (has a protocol like http://, https://, file://).
   * @private
   * @param {string} url - URL to check
   * @returns {boolean} True if URL is absolute
   */
  _isAbsoluteUrl(url) {
    return /^[a-z][a-z0-9+.-]*:/i.test(url);
  }

  // ---- Static worker infrastructure ----

  // ---- Request dispatch ----

  /**
   * Send a fetch-and-decode request to the dedicated fetch worker.
   * Returns a promise that resolves when the decode worker posts the result.
   * @private
   * @param {string} decodeOp - Worker operation name ('RCS_DECODE' | 'RCS_DECODE_BY_LEVEL')
   * @param {string|null} url - Absolute URL to fetch SVO data from
   * @param {number} offset - Byte offset in the file
   * @param {number} length - Number of bytes to read
   * @param {UmbrellaLeafNode} leafNode - The leaf node metadata
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @param {number} levelsInLod0 - Per-model LOD-0 source level count ('direct' mode only).
   * @param {string|null} [cacheHashKey] - OPFS key for raw SVO bytes (LOD ≥ 1 only)
   * @param {string|null} [bucketName] - OPFS bucket for `cacheHashKey`
   * @returns {Promise<any>}
   */
  _sendFetchAndDecode(decodeOp, url, offset, length, leafNode, lodLevel, levelsInLod0, cacheHashKey, bucketName) {
    return rcsWorkerPool.dispatchFetchAndDecode({
      decodeOp,
      url,
      offset,
      length,
      urn: /** @type {RcsFileLoader} */this.node.model.loader.getAuthUrn(),
      token: /** @type {RcsFileLoader} */this.node.model.loader.getToken(),
      acmSessionId: /** @type {RcsFileLoader} */this.node.model.loader.getAcmSessionId(),
      leafNode: {
        svoBounds: leafNode.svoBounds,
        numNonLeafNodes: leafNode.numNonLeafNodes,
        numSvoLeafNodes: leafNode.numSvoLeafNodes
      },
      lodLevel,
      levelsInLod0,
      interiorNodesIncluded: false,
      cacheHashKey,
      bucketName
    });

  }

  /**
   * Fetch + decode SVO payload via workers (single-phase from main-thread perspective).
   * @private
   * @param {string|null} url - Absolute URL to fetch SVO data from
   * @param {number} offset - Byte offset in the file
   * @param {number} length - Number of bytes to read
   * @param {UmbrellaLeafNode} leafNode - The leaf node metadata
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @param {number} levelsInLod0 - Per-model LOD-0 source level count ('direct' mode only).
   * @param {string|null} [cacheHashKey] - OPFS key for raw SVO bytes (LOD ≥ 1 only)
   * @param {string|null} [bucketName] - OPFS bucket for `cacheHashKey`
   * @returns {Promise<RcsDecodedPoints>}
   */
  async _decodeSvoPayloadInWorker(url, offset, length, leafNode, lodLevel, levelsInLod0, cacheHashKey, bucketName) {
    return this._sendFetchAndDecode('RCS_DECODE', url, offset, length, leafNode, lodLevel, levelsInLod0, cacheHashKey, bucketName);
  }

  /**
   * Fetch + decode-by-level SVO payload via workers.
   * @private
   * @param {string|null} url - Absolute URL to fetch SVO data from
   * @param {number} offset - Byte offset in the file
   * @param {number} length - Number of bytes to read
   * @param {UmbrellaLeafNode} leafNode - The leaf node metadata
   * @param {number} lodLevel - Target LOD level index for this fragment
   * @param {number} levelsInLod0 - Per-model LOD-0 source level count ('direct' mode only).
   * @param {string|null} [cacheHashKey] - OPFS key for the raw LOD-0 SVO bytes (transient bucket)
   * @param {string|null} [bucketName] - OPFS bucket for `cacheHashKey`
   * @returns {Promise<RcsLevelDecodeResult>}
   */
  async _decodeSvoPayloadByLevelInWorker(url, offset, length, leafNode, lodLevel, levelsInLod0, cacheHashKey, bucketName) {
    return this._sendFetchAndDecode('RCS_DECODE_BY_LEVEL', url, offset, length, leafNode, lodLevel, levelsInLod0, cacheHashKey, bucketName);
  }

  /**
   * Calculate the memory cost of loading this BVH node.
   * Returns the total memory required for all fragments in this node.
   * @returns {number} Total memory in bytes
   */
  getResourceCost() {
    const model = /** @type {Model} */this.node.model;
    const pkg = /** @type {RcsPackage} */model.getData();
    return pkg.computeNodeMemorySize(this.node.nodeId);
  }

  // ── OPFS cache (geometry activation) ─────────────────────────────────

  /**
   * Try to load an inner node's geometry from OPFS cache.
   * OPFS I/O is delegated to the loader's `opfsCache`; this method only
   * reconstructs fragment geometry from a cached vertex buffer.
   * @param {RcsFileLoader} rcsLoader
   * @param {number} fragId
   * @param {number} nodeId
   * @param {number} pointCount
   * @returns {Promise<boolean>}
   */
  async _loadInnerNodeFromCache(rcsLoader, fragId, nodeId, pointCount) {
    const vb = await rcsLoader.opfsCache.loadInnerNodePoints(nodeId, pointCount);
    if (!vb) return false;
    if (this.canceled) return false;

    const model = /** @type {Model} */rcsLoader.model;
    const fragmentList = model.getFragmentList();
    if (!fragmentList.getGeometry(fragId)) {
      // Inner-node fragments are intentionally not tracked in this.activatedFragments,
      // mirroring the cold-load path in execute(): inner nodes are never unloaded (they
      // stay resident for the session and back load-progress reporting), so undo() must
      // not deactivate them on demotion.
      rcsLoader._addAndActivateFragment(fragId, { pointCount, innerFragment: true });
    }
    const geometry = fragmentList.getGeometry(fragId);
    if (!geometry || !geometry.vb) return false;

    geometry.vb.set(vb, 0);
    geometry.vbNeedsUpdate = true;
    geometry.needsUpdate = true;
    rcsLoader.opfsCache.markInnerNodeCached(nodeId);

    return true;
  }

  /**
   * Deactivate fragments activated by this task when the BVH node is demoted.
   * Called by the out-of-core tile manager during stage rollback.
   */
  undo() {
    for (const fragId of this.activatedFragments) {
      const rcsLoader = /** @type {RcsFileLoader} */this.node.model.loader;
      rcsLoader._deactivateFragment(fragId);
    }

    this.activatedFragments = [];
  }
}